<template>
  <div v-if="!isSmWidth" id="top-navigation">
    <VAppBar
      id="app-bar"
      app
      fixed
      :height="defaultHeight"
      clipped-left
      :class="{
        [$style['app-bar']]: true,
        [$style['app-bar--supportuser']]: isSupportUser,
        [$style['app-bar--search']]: searchDialogIsVisible,
      }"
    >
      <div :class="$style.grid">
        <div :class="[$style.navigationGridItem, $style.navigation]">
          <TopNavigationMenu :navigation-items="topNavigationItems" />
          <FeatureSwitch name="fe-environment-info">
            <ApiOverviewInstances></ApiOverviewInstances>
          </FeatureSwitch>
        </div>

        <div :class="[$style.navigationGridItem, $style.search]">
          <AccountBlockNotification />
          <TopNavigationMenu
            :navigation-items="centerNavigationItemsBeforeSearch"
            :show-menu-button="false"
            :render-horizontally="true"
          >
          </TopNavigationMenu>
          <FlyoutNavigationSearch />
          <TopNavigationMenu
            :navigation-items="centerNavigationItems"
            :show-menu-button="false"
            :render-horizontally="true"
          >
          </TopNavigationMenu>
        </div>

        <div :class="[$style.navigationGridItem, $style.subnavigation]">
          <TopNavigationMenu
            :navigation-items="secondaryTopNavigationItems"
            :show-menu-button="false"
          />
          <MiniBasketFlyout :disabled="appShopModus">
            <MiniBasket :disabled="appShopModus" />
          </MiniBasketFlyout>
        </div>
      </div>
    </VAppBar>
  </div>
</template>

<script>
import { get, isUndefined, cloneDeep } from 'lodash-es'
import { mapGetters, mapState } from 'vuex'

import API_CONSTANTS from '@/js/helper/api-constants.js'

import { types as STOREMODULES } from '@/js/plugins/store/store-modules.js'
import APP_GETTERS from '@/js/plugins/store/modules/app/getter-types.js'
import APP_CONSTANTS from '@/js/plugins/store/modules/app/constants.js'

import { parseDataAttributeJson } from '@/js/helper/data-attribute-helper.js'
import navigationTargets from '@/js/components/Navigation/components/navigation-targets'

import MessageCenterLinktilesMixin from '@/js/mixins/message-center-linktiles-mixin.js'
import PermissionMixin from '@/js/mixins/permission-mixin.js'
import ScopeMixin from '@/js/mixins/scope-mixin.js'

import MiniBasket from '@/js/components/Basket/Mini/MiniBasket.vue'
import FlyoutNavigationSearch from '@/js/components/Search/FlyoutNavigationSearch.vue'
import MiniBasketFlyout from '@/js/components/Basket/Mini/MiniBasketFlyout.vue'
import TopNavigationMenu from '@/js/components/Navigation/TopNavigationMenu.vue'
import AccountBlockNotification from '@/js/components/Notifications/AccountBlockNotification.vue'
import ApiOverviewInstances from '@/js/components/Notifications/ApiOverviewInstances.vue'
import FeatureSwitch from '@/js/components/Shared/FeatureSwitch.js'

export default {
  name: 'TopNavigation',
  components: {
    MiniBasket,
    MiniBasketFlyout,
    TopNavigationMenu,
    FlyoutNavigationSearch,
    AccountBlockNotification,
    ApiOverviewInstances,
    FeatureSwitch,
  },
  mixins: [ScopeMixin, PermissionMixin, MessageCenterLinktilesMixin],
  props: {
    element: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      parsedElement: parseDataAttributeJson(this.element),
      // If you change these values. Chnage :global(#top) too! 80 + 110 = 190
      defaultHeight: 80,
    }
  },
  computed: {
    ...mapGetters(STOREMODULES.APP, {
      appShopModus: APP_GETTERS.SHOPMODUS,
      isSmWidth: APP_GETTERS.IS_SM_WIDTH,
      isSupportUser: APP_GETTERS.SUPPORT_USER,
    }),
    ...mapState(STOREMODULES.APP, {
      searchDialogIsVisible: APP_CONSTANTS.SEARCH_DIALOG_IS_VISIBLE,
    }),
    basketContainer() {
      return this.isSmWidth ? 'MiniBasketDialog' : 'MiniBasketFlyout'
    },
    navigationItems() {
      const navigationItems = get(
        this.parsedElement,
        [API_CONSTANTS.RELATIONSHIPS, API_CONSTANTS.ELEMENTS, API_CONSTANTS.DATA],
        [],
      )
        .map(this.addNavigationTargetInfo)
        .map(this.addSpecialMenus)
        .filter(i => i.show)

      return navigationItems
    },
    topNavigationItems() {
      return this.navigationItems.filter(
        el =>
          el.type === API_CONSTANTS.NAVIGATION.ICON_TYPE &&
          get(
            el,
            [API_CONSTANTS.ATTRIBUTES, API_CONSTANTS.NAVIGATION.ATTRIBUTE.NAVIGATION_SECONDARY],
            0,
          ) !== 1 &&
          !this.isCenterNavigationItem(el) &&
          !this.isCenterNavigationItemBeforeSearch(el),
      )
    },
    secondaryTopNavigationItems() {
      return this.navigationItems.filter(
        el =>
          get(
            el,
            [API_CONSTANTS.ATTRIBUTES, API_CONSTANTS.NAVIGATION.ATTRIBUTE.NAVIGATION_SECONDARY],
            0,
          ) === 1 && !this.isCenterNavigationItem(el),
      )
    },
    centerNavigationItems() {
      return this.navigationItems.filter(el => this.isCenterNavigationItem(el))
    },
    centerNavigationItemsBeforeSearch() {
      return this.navigationItems.filter(el => this.isCenterNavigationItemBeforeSearch(el))
    },
  },
  methods: {
    isCenterNavigationItem(el) {
      // Add component names here to show next to search bar
      const whitelist = ['NavigationProductsList']

      return whitelist.includes(
        get(
          el,
          [API_CONSTANTS.ATTRIBUTES, API_CONSTANTS.NAVIGATION.ATTRIBUTE.NAVIGATION_COMPONENT],
          '',
        ),
      )
    },
    isCenterNavigationItemBeforeSearch(el) {
      const whitelist = ['NavigationSerialNumberSearch']

      return whitelist.includes(
        get(
          el,
          [API_CONSTANTS.ATTRIBUTES, API_CONSTANTS.NAVIGATION.ATTRIBUTE.NAVIGATION_COMPONENT],
          '',
        ),
      )
    },
    addNavigationTargetInfo(navItem) {
      const component = get(navItem, [
        API_CONSTANTS.ATTRIBUTES,
        API_CONSTANTS.NAVIGATION.ATTRIBUTE.NAVIGATION_COMPONENT,
      ])
      const perm = get(navigationTargets, [component, 'perm'])
      const storeCheck = get(navigationTargets, [component, 'storeCheck'])
      const show =
        (storeCheck ? storeCheck(this.$store) : true) && (perm ? this.hasRole(perm) : true)
      // console.log({ component, perm, show })
      const result = { ...cloneDeep(navItem), show }

      const icon = get(result, [API_CONSTANTS.ATTRIBUTES, API_CONSTANTS.ICON.NAME])
      const linkTiles =
        icon === 'account' || icon === 'complaint'
          ? get(
              result,
              [API_CONSTANTS.ATTRIBUTES, API_CONSTANTS.NAVIGATION.ATTRIBUTE.NAVIGATION_LINK_TILES],
              [],
            )
          : get(result, [
              API_CONSTANTS.ATTRIBUTES,
              API_CONSTANTS.NAVIGATION.ATTRIBUTE.NAVIGATION_LINK_TILES,
            ])
      if (linkTiles) {
        result[API_CONSTANTS.ATTRIBUTES][API_CONSTANTS.NAVIGATION.ATTRIBUTE.NAVIGATION_LINK_TILES] =
          linkTiles.map(this.addNavigationTargetInfo).filter(i => i.show)
      }
      const hadLinktilesButNothingIsShown =
        linkTiles &&
        !result[API_CONSTANTS.ATTRIBUTES][API_CONSTANTS.NAVIGATION.ATTRIBUTE.NAVIGATION_LINK_TILES]
          .length

      if (hadLinktilesButNothingIsShown) {
        result.show = false
      }
      return result
    },
    addSpecialMenus(navigationItem) {
      const linkTiles =
        navigationItem.attributes?.component === 'NavigationMessageCenter'
          ? this.generateMessageCenterLinkTiles()
          : navigationItem.attributes?.component === 'NavigationProductsList'
          ? this.generateProductsListLinkTiles()
          : undefined

      if (isUndefined(linkTiles)) {
        return navigationItem
      }

      if (linkTiles.length) {
        navigationItem.attributes['link_tiles'] = linkTiles
        navigationItem.show = true
      } else {
        navigationItem.show = false
      }
      return navigationItem
    },
  },
}
</script>
<style lang="scss" module>
.app-bar {
  display: none;

  background-color: var(--white) !important;
}

.app-bar {
  @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
    display: block;
    overflow: hidden;

    &--search {
      overflow: visible;
    }

    &--supportuser {
      background: linear-gradient(
        90deg,
        var(--red-lighten-2) 0%,
        rgba(255, 255, 255, 1) 7%,
        rgba(255, 255, 255, 1) 100%
      ) !important;
      background-color: var(--red) !important;
    }

    :global(.v-toolbar__content) {
      padding: 0 !important;
    }
  }
}

.grid {
  display: grid;
  grid-template-areas: 'navigation search subnavigation';
  grid-template-columns: repeat(3, 1fr);
  width: 100%;
  height: 100%;
  overflow-x: auto;
  overflow-y: hidden;

  // Vuetify-Overwrite, else container gets too high
  line-height: 0 !important;

  column-gap: 0.5rem;
}

.navigationGridItem {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.navigation {
  grid-area: navigation;
  justify-content: flex-start;
}

.search {
  grid-area: search;
  justify-content: center;
}

.subnavigation {
  grid-area: subnavigation;
  justify-content: flex-end;
}

:global(.base-layout--hide-navigation) .app-bar {
  display: none;
}
</style>
